/**
 * AdminHieConsole — HIE pull audit console (iter 171).
 *
 * Every Metriport pull across every intake in one filterable list.
 * Shows patient, referring facility, mode (mock/live), status, counts,
 * and a link back to the intake drawer.
 *
 * Route: /admin/hie (admin + provider org roles)
 * Testids: admin-hie-page, hie-console-row-<intake_id>, hie-console-filter-<status>
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Loader2, RefreshCw, Filter, Shield,
  Inbox, Building2, Users, FileText, Stethoscope, Pill, FlaskConical, Hospital,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Shield, label: 'Compliance', href: '/admin/compliance' },
  { icon: ShieldCheck, label: 'AI Validation', href: '/admin/ai-validation' },
  { icon: Inbox, label: 'Intake Inbox', href: '/admin/intake' },
  { icon: Building2, label: 'Referral Sources', href: '/admin/referral-sources' },
  { icon: Users, label: 'CRM', href: '/admin/crm' },
  { icon: ShieldCheck, label: 'HIE Console', href: '/admin/hie', active: true },
];

const STATUS_STYLE = {
  completed:  'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  processing: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  failed:     'bg-rose-500/15 text-rose-300 border-rose-500/30',
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLE[status] || 'bg-slate-500/15 text-slate-400 border-slate-500/30';
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${cls}`}>
      {status}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function Kpi({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-xl font-semibold tabular-nums text-slate-100 mt-0.5">{value ?? 0}</div>
    </div>
  );
}

export default function AdminHieConsole() {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState('mock');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/hie/console`, {
        ...getAuthHeader(),
        params: { include_all_orgs: true, status: filter || undefined },
      });
      setRows(data.rows || []);
      setMode(data.mode || 'mock');
    } catch (e) {
      toast.error('Could not load HIE console');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, filter]);

  useEffect(() => { load(); }, [load]);

  const totals = rows.reduce((acc, r) => {
    acc.count += 1;
    if (r.status === 'completed')  acc.completed += 1;
    if (r.status === 'processing') acc.processing += 1;
    if (r.status === 'failed')     acc.failed += 1;
    return acc;
  }, { count: 0, completed: 0, processing: 0, failed: 0 });

  return (
    <DashboardShell navItems={ADMIN_NAV}>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="admin-hie-page">
        <header className="flex flex-wrap items-end gap-3 justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-100 tracking-tight flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-cn-primary" /> HIE Console
              {mode === 'mock' && (
                <span className="ml-2 uppercase text-[10px] tracking-widest bg-amber-500/20 text-amber-300 rounded px-2 py-0.5">
                  MOCK MODE
                </span>
              )}
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-xl">
              Every Metriport pull across every intake. HIE queries fire on accept when
              patient consent is captured, and can also be triggered manually from the intake drawer.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Filter className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500 pointer-events-none" />
              <select
                value={filter}
                onChange={(ev) => setFilter(ev.target.value)}
                className="pl-7 pr-3 py-1.5 bg-slate-900 border border-slate-800 rounded-md text-sm text-slate-100 focus:outline-none focus:border-cn-primary/60"
                data-testid="hie-console-filter"
              >
                <option value="">All statuses</option>
                <option value="completed">Completed</option>
                <option value="processing">Processing</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <Button onClick={load} variant="outline" size="sm" data-testid="hie-console-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </header>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={ShieldCheck} label="Total pulls"    value={totals.count}      testId="hie-kpi-total" />
          <Kpi icon={Stethoscope} label="Completed"      value={totals.completed}  testId="hie-kpi-completed" />
          <Kpi icon={Loader2}     label="Processing"     value={totals.processing} testId="hie-kpi-processing" />
          <Kpi icon={FlaskConical} label="Failed"        value={totals.failed}     testId="hie-kpi-failed" />
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-slate-500" /> HIE pulls ({rows.length})
            </h2>
          </div>
          {loading ? (
            <div className="p-10 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="hie-console-empty">
              No HIE pulls yet. Accept an intake with consent, or press &quot;Pull HIE records&quot; from the intake drawer.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-800">
                  <th className="px-4 py-2 font-medium">Patient</th>
                  <th className="px-4 py-2 font-medium">Referring</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium text-right">H&Ps</th>
                  <th className="px-4 py-2 font-medium text-right">Dx</th>
                  <th className="px-4 py-2 font-medium text-right">Meds</th>
                  <th className="px-4 py-2 font-medium text-right">Labs</th>
                  <th className="px-4 py-2 font-medium text-right">Hosp.</th>
                  <th className="px-4 py-2 font-medium">Updated</th>
                  <th className="px-4 py-2 font-medium text-right"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const c = r.summary?.counts || {};
                  return (
                    <tr key={r.intake_id}
                        className="border-b border-slate-800/60 hover:bg-slate-800/30"
                        data-testid={`hie-console-row-${r.intake_id}`}>
                      <td className="px-4 py-3 font-medium text-slate-100">{r.patient_name || 'Unknown'}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">{r.referring_facility || '—'}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3 text-right tabular-nums">{c.hnps ?? '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{c.diagnoses ?? '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{c.medications ?? '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{c.labs ?? '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{c.prior_hospitalizations ?? '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(r.updated_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/intake?open=${r.intake_id}`}
                          className="text-xs text-cn-primary hover:underline"
                          data-testid={`hie-console-open-${r.intake_id}`}
                        >
                          Open intake →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </DashboardShell>
  );
}
